import { stringifyGraph } from '@/clients/http'
import { CustomerModel } from '@/http/models/customer'

import { useCallback, useEffect, useState } from 'react'

import { CustomerModel as CustomerModelType } from '@/types'

interface UseFetchCustomerListProps {
  search?: string
  status?: 'active' | 'inactive' | 'all'
  pageSize?: number
  autoFetch?: boolean
  debounceMs?: number
}

export const useFetchCustomerList = ({
  search = '',
  status = 'active',
  pageSize = 100,
  autoFetch = true,
  debounceMs = 400,
}: UseFetchCustomerListProps = {}) => {
  const [customers, setCustomers] = useState<CustomerModelType.Customer[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [debouncedSearch, setDebouncedSearch] = useState(search)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
    }, debounceMs)

    return () => clearTimeout(timer)
  }, [search, debounceMs])

  const fetchCustomers = useCallback(
    async (searchQuery?: string) => {
      setLoading(true)
      setError(null)
      try {
        const response = await CustomerModel.getList({
          query: {
            page: 1,
            length: pageSize,
            search: searchQuery !== undefined ? searchQuery : debouncedSearch || undefined,
            status: status === 'all' ? undefined : status,
            graph_enc: stringifyGraph({
              status: true,
              name: true,
              email: true,
              phone: true,
              is_blocked: true,
            }),
          },
        })

        if (response.success && response.data?.result) {
          setCustomers(response.data.result)
        } else {
          setCustomers([])
        }
      } catch (err) {
        console.error('Failed to fetch customers:', err)
        setError('Failed to load customers')
        setCustomers([])
      } finally {
        setLoading(false)
      }
    },
    [debouncedSearch, status, pageSize]
  )

  useEffect(() => {
    if (autoFetch) {
      fetchCustomers()
    }
  }, [autoFetch, fetchCustomers])

  return {
    customers,
    loading,
    error,
    fetchCustomers,
    refetch: fetchCustomers,
  }
}
